import * as React from 'react'

import { TriangleAlertIcon } from 'lucide-react'
import { runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'

import UnsafeNotificationsListItem from '~/client/src/desktop/views/Notifications/components/NotificationsListItem'
import { CardList } from '~/client/src/shared/components/CardList'
import { withErrorBoundary } from '~/client/src/shared/components/ErrorBoundary'
import { Loader } from '~/client/src/shared/components/Loader'
import { clearSelection } from '~/client/src/shared/lists/core'
import { NotificationsListStore } from '~/client/src/shared/lists/notifications/NotificationsList.store'
import { useListInteraction } from '~/client/src/shared/lists/useListInteraction'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import NotificationsStore from '~/client/src/shared/stores/domain/Notifications.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import CommonStore from '~/client/src/shared/stores/ui/Common.store'

import DesktopInitialState from '../../../../stores/DesktopInitialState'

const NotificationsListItem = withErrorBoundary(UnsafeNotificationsListItem)
const DEFAULT_ITEM_HEIGHT = 70

interface IProps {
  notificationsListStore: NotificationsListStore
  notificationsStore: NotificationsStore
  showImportantColumn: boolean
  common?: CommonStore
  state?: DesktopInitialState
}

const NotificationsList = observer((props: IProps): JSX.Element => {
  const { notificationsListStore, notificationsStore, state } = props

  const activateNotification = React.useCallback(
    (recordId: string): void => {
      const row = notificationsListStore.list.filteredRecords.find(
        item => item.id === recordId,
      )
      if (!row) return

      runInAction(() => {
        if (notificationsStore.displayedNotification?.id !== row.id) {
          notificationsStore.markDisplayedAsRead()
          notificationsStore.displayedNotification = row.notification
        } else {
          notificationsStore.displayedNotification = null
          state.displayedContentObjectId = null
          notificationsListStore.list.dispatch(clearSelection())
        }
      })
    },
    [notificationsListStore, notificationsStore, state],
  )

  const interaction = useListInteraction(notificationsListStore.list, {
    onActivate: activateNotification,
    activateSelects: true,
    scopeKey: state.activeProject?.id,
  })

  const toggleNotification = React.useCallback(
    (id: string, event?: React.MouseEvent): void => {
      interaction.toggleFor(id, event)
    },
    [interaction],
  )

  const loading = state.loading
  const isLoading =
    loading.get(e.MARK_NOTIFICATION_AS_READ) ||
    loading.get(e.MARK_NOTIFICATIONS_AS_ARCHIVED) ||
    loading.get(e.MARK_NOTIFICATIONS_AS_READ) ||
    loading.get(e.LOAD_DELIVERIES) ||
    loading.get(e.LOAD_AND_LISTEN_TO_PERMIT_TYPES)

  return (
    <CardList
      items={[...notificationsListStore.list.filteredRecords]}
      renderItem={row => (
        <NotificationsListItem
          recordId={row.id}
          notification={row.notification}
          notificationsListStore={notificationsListStore}
          showImportantColumn={props.showImportantColumn}
          interactionHandlers={{
            ...interaction.handlersFor(row.id),
            onSelectionMouseDown: interaction.onSelectionMouseDown,
          }}
          toggleNotification={toggleNotification}
          activateNotification={interaction.activateFor}
        />
      )}
      loading={!!isLoading}
      loadingComponent={<Loader />}
      empty={renderEmpty()}
      className="full-height"
      estimatedItemHeight={DEFAULT_ITEM_HEIGHT}
    />
  )
})

function renderEmpty(): JSX.Element {
  return (
    <div className="full-height row x-center y-center text huge">
      <TriangleAlertIcon size={20} className="warning-icon mr5" />
      {Localization.translator.noNotificationsPassedFilters}
    </div>
  )
}

export default inject('common', 'state')(NotificationsList)
